import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

export type AuctionStatus = 'active' | 'settled' | 'expired_no_reserve' | 'cancelled' | 'settlement_stuck';

export interface Auction {
  id: string;
  nft_token_id: string;
  seller_address: string;
  sell_offer_id: string | null;
  min_bid_drops: string;
  reserve_price_drops: string;
  expires_at: string; // ISO
  status: AuctionStatus;
  winning_offer_id: string | null;
  winning_bid_drops: string | null;
  winning_bidder_address: string | null;
  settlement_tx_hash: string | null;
  settled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuctionBid {
  offer_id: string;
  amount_drops: string;
  owner_address: string;
  created_at: string;
  expiration: string | null;
}

export type XamanPayloadRef = XamanPayloadResponse;

/** Phase 4 signing intent — always present; carries client-signing txJson for joey-wc. */
export interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

export interface CreateAuctionRequest {
  nftTokenId: string;
  minBidDrops: string;
  reservePriceDrops: string;
  expiresAt: string; // ISO
}

export interface CreateAuctionResponse extends ApiResponse<{
  auction: Auction;
  signingIntent?: SigningIntentRef;
  payload?: XamanPayloadRef;
}> {}

export interface AuctionDetailResponse extends ApiResponse<{
  auction: Auction;
  bids: AuctionBid[];
}> {}

export interface AuctionByNftResponse extends ApiResponse<{
  auction: Auction | null;
  bids: AuctionBid[];
}> {}

export interface PlaceBidRequest {
  bidAmountDrops: string;
}

export interface PlaceBidResponse extends ApiResponse<{
  previousHighestBidDrops: string;
  signingIntent?: SigningIntentRef;
  payload?: XamanPayloadRef;
}> {}

export interface ConfirmTxRequest {
  txHash: string;
}

export interface ConfirmAuctionResponse extends ApiResponse<{
  auction: Auction;
}> {}

export interface ConfirmBidResponse extends ApiResponse<{
  offerId: string;
}> {}

export interface CancelAuctionResponse extends ApiResponse<{
  auction: Auction;
  signingIntent?: SigningIntentRef;
  payload?: XamanPayloadRef | null;
}> {}

const wrap = <T,>(label: string) =>
  async (op: () => Promise<T>): Promise<T | { success: false; error: string }> => {
    try {
      return await op();
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : `Failed to ${label}`,
      } as any;
    }
  };

export async function createAuction(req: CreateAuctionRequest) {
  return wrap<CreateAuctionResponse>('create auction')(() =>
    apiClient.post<CreateAuctionResponse>('/auctions', req)
  );
}

export async function confirmAuctionListing(auctionId: string, txHash: string) {
  return wrap<ConfirmAuctionResponse>('confirm auction listing')(() =>
    apiClient.post<ConfirmAuctionResponse>(`/auctions/${auctionId}/confirm`, { txHash })
  );
}

export async function placeAuctionBid(auctionId: string, req: PlaceBidRequest) {
  return wrap<PlaceBidResponse>('place bid')(() =>
    apiClient.post<PlaceBidResponse>(`/auctions/${auctionId}/bids`, req)
  );
}

export async function confirmAuctionBid(auctionId: string, txHash: string) {
  return wrap<ConfirmBidResponse>('confirm bid')(() =>
    apiClient.post<ConfirmBidResponse>(`/auctions/${auctionId}/bids/confirm`, { txHash })
  );
}

export async function cancelAuction(auctionId: string) {
  return wrap<CancelAuctionResponse>('cancel auction')(() =>
    apiClient.post<CancelAuctionResponse>(`/auctions/${auctionId}/cancel`, {})
  );
}

export async function confirmAuctionCancellation(auctionId: string) {
  return wrap<ConfirmAuctionResponse>('confirm cancellation')(() =>
    apiClient.post<ConfirmAuctionResponse>(`/auctions/${auctionId}/cancel/confirm`, {})
  );
}

export async function getAuctionByNft(nftTokenId: string) {
  return wrap<AuctionByNftResponse>('fetch auction')(() =>
    apiClient.get<AuctionByNftResponse>(`/auctions/by-nft/${nftTokenId}`)
  );
}

export async function getAuction(auctionId: string) {
  return wrap<AuctionDetailResponse>('fetch auction')(() =>
    apiClient.get<AuctionDetailResponse>(`/auctions/${auctionId}`)
  );
}
